import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Edit, X, Upload, User } from "lucide-react";
import { api } from "../../lib/api";
import { SearchableSelect } from "../../components/SearchableSelect";

export function AdminVenuesPage() {
    const { t } = useTranslation();
    const [venues, setVenues] = useState([]);
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [editing, setEditing] = useState(null); // null or a venue object
    const [form, setForm] = useState({ name: "", address: "", zip_code: "", country_id: "", state_id: "", city_id: "" });

    const load = async () => {
        const { data } = await api.get("/api/venues");
        setVenues(data || []);
    };
    useEffect(() => {
        load();
        api.get("/api/countries").then(({ data }) => setCountries(data || []));
    }, []);

    useEffect(() => {
        if (form.country_id) api.get("/api/states", { params: { country_id: form.country_id } }).then(({ data }) => setStates(data || []));
        else setStates([]);
    }, [form.country_id]);
    useEffect(() => {
        if (form.state_id) api.get("/api/cities", { params: { state_id: form.state_id } }).then(({ data }) => setCities(data || []));
        else setCities([]);
    }, [form.state_id]);

    const save = async () => {
        try {
            if (editing) await api.put(`/api/admin/venues/${editing.id}`, form);
            else await api.post("/api/admin/venues", form);
            toast.success(t("common.success"));
            setEditing(null);
            setForm({ name: "", address: "", zip_code: "", country_id: "", state_id: "", city_id: "" });
            load();
        } catch (e) {
            toast.error(t("common.error"));
        }
    };

    const remove = async (id) => {
        try { await api.delete(`/api/admin/venues/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail); }
    };

    return (
        <div data-testid="admin-venues-page" className="space-y-6">
            <h1 className="font-heading font-black text-3xl">{t("admin.venues")}</h1>
            <div className="bento-card p-5 space-y-3">
                <h2 className="font-bold">{editing ? t("admin.edit") : t("admin.add")}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input data-testid="venue-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("admin.name")} className="input-modern" />
                    <input data-testid="venue-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t("venue.address")} className="input-modern" />
                    <input data-testid="venue-zip" value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder={t("venue.zip_code")} className="input-modern" />
                    <SearchableSelect testId="venue-country" options={countries.map((c) => ({ value: c.id, label: c.name }))} value={form.country_id} onValueChange={(v) => setForm({ ...form, country_id: v, state_id: "", city_id: "" })} placeholder={t("search.country")} />
                    <SearchableSelect testId="venue-state" options={states.map((s) => ({ value: s.id, label: s.name }))} value={form.state_id} onValueChange={(v) => setForm({ ...form, state_id: v, city_id: "" })} placeholder={t("search.state")} disabled={!form.country_id} />
                    <SearchableSelect testId="venue-city" options={cities.map((c) => ({ value: c.id, label: c.name }))} value={form.city_id} onValueChange={(v) => setForm({ ...form, city_id: v })} placeholder={t("search.city")} disabled={!form.state_id} />
                </div>
                <div className="flex gap-2">
                    <button data-testid="venue-save" onClick={save} className="btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" /> {t("admin.save")}</button>
                    {editing && <button onClick={() => { setEditing(null); setForm({ name: "", address: "", zip_code: "", country_id: "", state_id: "", city_id: "" }); }} className="btn-outline"><X className="h-4 w-4" /></button>}
                </div>
            </div>
            <div className="bento-card overflow-hidden">
                <div className="divide-y divide-border">
                    {venues.map((v) => (
                        <div key={v.id} data-testid={`venue-row-${v.id}`} className="flex items-center justify-between p-4 gap-3">
                            <div className="flex-1">
                                <div className="font-bold">{v.name}</div>
                                <div className="text-sm text-muted-foreground">
                                    {[v.address, v.zip_code, v.city?.name, v.state?.name, v.country?.name].filter(Boolean).join(", ")}
                                </div>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => { setEditing(v); setForm({ name: v.name, address: v.address, zip_code: v.zip_code || "", country_id: v.country_id, state_id: v.state_id, city_id: v.city_id }); }} className="p-2 hover:bg-muted rounded-lg"><Edit className="h-4 w-4" /></button>
                                <button onClick={() => remove(v.id)} className="p-2 hover:bg-destructive/10 text-destructive rounded-lg"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

export function AdminArtistsPage() {
    const { t } = useTranslation();
    const [artists, setArtists] = useState([]);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState("");
    const fileRefs = useRef({});

    const load = async () => {
        const { data } = await api.get("/api/artists");
        setArtists(data || []);
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!name) return;
        try {
            if (editing) await api.put(`/api/admin/artists/${editing.id}`, { name });
            else await api.post("/api/admin/artists", { name });
            setEditing(null);
            setName("");
            load();
        } catch (e) {
            toast.error(t("common.error"));
        }
    };

    const uploadPhoto = async (id, file) => {
        const form = new FormData();
        form.append("file", file);
        try {
            await api.post(`/api/admin/artists/${id}/photo`, form, { headers: { "Content-Type": "multipart/form-data" } });
            load();
        } catch {}
    };
    const removePhoto = async (id) => { await api.delete(`/api/admin/artists/${id}/photo`); load(); };
    const remove = async (id) => { try { await api.delete(`/api/admin/artists/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail); } };

    return (
        <div data-testid="admin-artists-page" className="space-y-6">
            <h1 className="font-heading font-black text-3xl">{t("admin.artists")}</h1>
            <div className="bento-card p-5 space-y-3">
                <div className="flex gap-2 max-w-md">
                    <input data-testid="artist-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin.name")} className="input-modern flex-1" />
                    <button data-testid="artist-save-btn" onClick={save} className="btn-primary inline-flex items-center gap-2"><Plus className="h-4 w-4" /></button>
                    {editing && <button onClick={() => { setEditing(null); setName(""); }} className="btn-outline !py-2 !px-3"><X className="h-4 w-4" /></button>}
                </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {artists.map((a) => (
                    <div key={a.id} data-testid={`artist-${a.id}`} className="bento-card p-4 space-y-2 text-center">
                        <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-muted">
                            {a.photo_url ? <img src={a.photo_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><User className="h-6 w-6 text-muted-foreground" /></div>}
                        </div>
                        <div className="font-semibold truncate">{a.name}</div>
                        <div className="flex flex-wrap gap-1 justify-center">
                            <input ref={(el) => (fileRefs.current[a.id] = el)} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadPhoto(a.id, e.target.files[0])} />
                            <button onClick={() => fileRefs.current[a.id]?.click()} className="p-1.5 hover:bg-muted rounded-lg" title="Upload photo"><Upload className="h-3 w-3" /></button>
                            {a.photo_url && <button onClick={() => removePhoto(a.id)} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-lg" title="Remove photo"><X className="h-3 w-3" /></button>}
                            <button onClick={() => { setEditing(a); setName(a.name); }} className="p-1.5 hover:bg-muted rounded-lg"><Edit className="h-3 w-3" /></button>
                            <button onClick={() => remove(a.id)} className="p-1.5 hover:bg-destructive/10 text-destructive rounded-lg"><Trash2 className="h-3 w-3" /></button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
